import { useState, useMemo, useEffect, useRef } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { annotateAttachmentUrl } from "@/lib/attachment-url";
import {
  useAttachmentRateLimit,
  formatRateLimitCountdown,
} from "@/hooks/use-attachment-rate-limit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { useAbortRef, useIsMountedRef, isAbortError } from "@/hooks/use-cancellable-mutation";
import { AlertCircle, FileText, Image as ImageIcon, Loader2, Paperclip, Search, Send, User, X } from "lucide-react";
import type { Member, Property } from "@shared/schema";
import { CannedResponsePicker } from "@/components/canned-response-picker";
import {
  recordCannedUsesIfPresent,
  type PendingCannedUse,
} from "@/lib/canned-response-usage";
import { getMemberDisplayName } from "@shared/member-display";
import { PadsplitSendErrorDialog, isPadsplitSendError } from "@/components/padsplit-send-error-dialog";
import { MessagingOutageBanner } from "@/components/messaging-outage-banner";
import { useMessagingOutage } from "@/hooks/use-messaging-outage";
import { uploadMimeType } from "@/lib/upload-mime";
import { MessagePlaceholderPreview } from "@/components/message-placeholder-preview";
import type { MessageVarContext } from "@/lib/message-placeholders";

type MemberWithCurrent = Member & { currentPropertyId?: number | null };

const ALLOWED_ATTACHMENT_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/heic",
  "image/heif",
  "application/pdf",
]);
const ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_MAX_COUNT = 10;

type PendingAttachment = {
  id: string;
  name: string;
  mimeType: string;
  size: number;
  status: "uploading" | "ready" | "error";
  objectPath?: string;
  errorMessage?: string;
};

interface NewMessageModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Task #1029 — On send we no longer return a conversationId (conversations
  // table is gone). Callers receive the memberId so they can navigate to the
  // member's messages thread instead.
  onSent?: (memberId: number) => void;
  presetMemberId?: number | null;
}

export function NewMessageModal({ open, onOpenChange, onSent, presetMemberId }: NewMessageModalProps) {
  const { toast } = useToast();
  const { enabled: messagingOutage } = useMessagingOutage();
  const [padsplitErrorOpen, setPadsplitErrorOpen] = useState(false);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(presetMemberId ?? null);
  const [search, setSearch] = useState("");
  const [messageText, setMessageText] = useState("");
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Task #2120 — Tag each canned-response insertion so we can record usage
  // only after the message is actually sent. Reset on close and on send.
  const [pendingCannedUses, setPendingCannedUses] = useState<PendingCannedUse[]>([]);

  function insertAtCursor(text: string, cannedId?: number) {
    const el = messageTextareaRef.current;
    if (!el) {
      setMessageText((prev) => prev + (prev && !prev.endsWith("\n") ? "\n" : "") + text);
    } else {
      const start = el.selectionStart ?? messageText.length;
      const end = el.selectionEnd ?? messageText.length;
      const current = el.value;
      setMessageText(current.slice(0, start) + text + current.slice(end));
      setTimeout(() => {
        el.focus();
        const newCursor = start + text.length;
        el.setSelectionRange(newCursor, newCursor);
      }, 0);
    }
    if (cannedId != null) {
      setPendingCannedUses((prev) => [...prev, { id: cannedId, insertedText: text }]);
    }
  }
  const [attachments, setAttachments] = useState<PendingAttachment[]>([]);
  // Task #951 — When the per-user attachment quota (URLs/hour or
  // bytes/hour) trips, the server returns HTTP 429 with `code: RATE_LIMITED`
  // and a `retryAfterMs` hint. We surface this as an inline countdown
  // banner above the Send button (matching the login-page rate-limit
  // treatment) so hosts know it's a quota — not a bug — and exactly when
  // they can retry. The shared `useAttachmentRateLimit` hook owns the
  // state machine so both composers stay in sync.
  const rateLimit = useAttachmentRateLimit();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Task #2367 — Track in-flight controllers so a Cancel button can
  // abort the underlying fetch (attachment upload presign + PUT, or the
  // final send) instead of just blocking dismissal behind a modal.
  const isMountedRef = useIsMountedRef();
  const sendAbort = useAbortRef();
  const attachmentAbortsRef = useRef<Map<string, AbortController>>(new Map());

  useEffect(() => {
    if (open) {
      setSelectedMemberId(presetMemberId ?? null);
      setSearch("");
      setMessageText("");
      setAttachments([]);
      setPendingCannedUses([]);
      rateLimit.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, presetMemberId]);

  function makeAttachmentId(): string {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const remaining = ATTACHMENT_MAX_COUNT - attachments.length;
    if (remaining <= 0) {
      toast({
        title: "Attachment limit reached",
        description: `You can attach up to ${ATTACHMENT_MAX_COUNT} files.`,
        variant: "destructive",
      });
      return;
    }
    const incoming = Array.from(files).slice(0, remaining);

    for (const file of incoming) {
      const id = makeAttachmentId();
      // Resolve the type BEFORE the allow-list check — browsers report an
      // EMPTY file.type for .heic picks, so has("") rejected a photo the list
      // actually permits. Mirrors the composer in MemberTimeline.
      const resolvedMime = uploadMimeType(file);
      if (!ALLOWED_ATTACHMENT_MIME.has(resolvedMime)) {
        setAttachments((prev) => [
          ...prev,
          {
            id,
            name: file.name,
            mimeType: resolvedMime,
            size: file.size,
            status: "error",
            errorMessage: "Only images and PDFs are allowed",
          },
        ]);
        continue;
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        setAttachments((prev) => [
          ...prev,
          {
            id,
            name: file.name,
            mimeType: resolvedMime,
            size: file.size,
            status: "error",
            errorMessage: `File is over ${Math.round(ATTACHMENT_MAX_BYTES / (1024 * 1024))}MB`,
          },
        ]);
        continue;
      }

      setAttachments((prev) => [
        ...prev,
        { id, name: file.name, mimeType: resolvedMime, size: file.size, status: "uploading" },
      ]);

      const controller = new AbortController();
      attachmentAbortsRef.current.set(id, controller);
      try {
        const urlRes = await apiRequest(
          "POST",
          "/api/app/messages/upload-url",
          {},
          { signal: controller.signal },
        );
        const { uploadUrl, objectPath } = (await urlRes.json()) as {
          uploadUrl: string;
          objectPath: string;
        };
        const putRes = await fetch(uploadUrl, {
          method: "PUT",
          headers: { "Content-Type": resolvedMime },
          body: file,
          signal: controller.signal,
        });
        if (!putRes.ok) {
          throw new Error(`Upload failed (${putRes.status})`);
        }
        if (!isMountedRef.current) return;
        setAttachments((prev) =>
          prev.map((a) => (a.id === id ? { ...a, status: "ready", objectPath } : a)),
        );
      } catch (err: any) {
        if (isAbortError(err)) {
          // Task #2367 — user cancelled; drop the placeholder row.
          if (isMountedRef.current) {
            setAttachments((prev) => prev.filter((a) => a.id !== id));
          }
        } else if (isMountedRef.current) {
          setAttachments((prev) =>
            prev.map((a) =>
              a.id === id
                ? { ...a, status: "error", errorMessage: parseErrorMessage(err, "Upload failed") }
                : a,
            ),
          );
        }
      } finally {
        attachmentAbortsRef.current.delete(id);
      }
    }

    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  function removeAttachment(id: string) {
    // Task #2367 — If the attachment is mid-upload, also tear down its
    // in-flight presign + PUT so we don't hold a network slot for a
    // result the user no longer wants.
    const controller = attachmentAbortsRef.current.get(id);
    if (controller) {
      attachmentAbortsRef.current.delete(id);
      controller.abort();
    }
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }

  function cancelAllAttachmentUploads() {
    const controllers = Array.from(attachmentAbortsRef.current.values());
    attachmentAbortsRef.current.clear();
    for (const c of controllers) c.abort();
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  const readyAttachments = attachments.filter((a) => a.status === "ready");
  const hasUploading = attachments.some((a) => a.status === "uploading");
  const hasErrors = attachments.some((a) => a.status === "error");

  const membersQuery = useQuery<MemberWithCurrent[]>({
    queryKey: ["/api/app/devices/members"],
    enabled: open,
  });

  const propertiesQuery = useQuery<Property[]>({
    queryKey: ["/api/app/properties"],
    enabled: open,
  });

  const propertyById = useMemo(() => {
    const map = new Map<number, Property>();
    for (const p of propertiesQuery.data || []) map.set(p.id, p);
    return map;
  }, [propertiesQuery.data]);

  // Task #1996 — the New Message picker only shows active members. Moved-out
  // members are intentionally hidden so hosts can't accidentally compose to
  // an archived occupant; historical threads remain reachable from the
  // member detail page.
  const allMembers = (membersQuery.data || []).filter((m) => m.status === "active");

  const resolveCurrentPropertyId = (m: MemberWithCurrent): number | null =>
    (m.currentPropertyId ?? null);

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return allMembers;
    return allMembers.filter((m) => {
      const propId = resolveCurrentPropertyId(m);
      const property = propId ? propertyById.get(propId) : undefined;
      const propName = property?.name || property?.address || "";
      return (
        getMemberDisplayName(m).toLowerCase().includes(term) ||
        String(m.name ?? "").toLowerCase().includes(term) ||
        String(m.legalName ?? "").toLowerCase().includes(term) ||
        String(m.phone ?? "").toLowerCase().includes(term) ||
        String(m.email ?? "").toLowerCase().includes(term) ||
        propName.toLowerCase().includes(term)
      );
    });
  }, [allMembers, propertyById, search]);

  const selectedMember = useMemo(
    () => allMembers.find((m) => m.id === selectedMemberId) || null,
    [allMembers, selectedMemberId],
  );

  const recipientContext = useMemo<Partial<MessageVarContext> | null>(() => {
    if (!selectedMember) return null;
    const propId = resolveCurrentPropertyId(selectedMember);
    const prop = propId ? propertyById.get(propId) : undefined;
    const fullName = getMemberDisplayName(selectedMember);
    const firstName = fullName.trim().split(/\s+/)[0] || fullName;
    return {
      memberFirstName: firstName || null,
      memberFullName: fullName || null,
      propertyName: prop?.name ?? null,
      propertyFullAddress: prop?.address ?? null,
    };
  }, [selectedMember, propertyById]);

  function getPropertyLabel(member: MemberWithCurrent): string {
    const propId = resolveCurrentPropertyId(member);
    if (!propId) return "";
    const p = propertyById.get(propId);
    if (!p) return "";
    return p.name || p.address || "";
  }

  const sendMutation = useMutation({
    // Task #1029 — Outbound messages are now enqueued onto messages_pending
    // and harvested back into member_chats by the worker + reconciler.
    mutationFn: async () => {
      if (!selectedMemberId) throw new Error("Select a member first");
      // Task #1018 — Annotate each upload with its original filename via a
      // `?fn=<encoded>` query string so the server-side sanitizer can persist
      // it alongside the existing `?ct=` content-type annotation. The
      // lightbox Download action then saves the file with the human-chosen
      // name instead of the storage object's UUID path segment.
      const attachmentUrls = readyAttachments
        .map((a) => a.objectPath ? annotateAttachmentUrl(a.objectPath, a.name) : null)
        .filter((p): p is string => !!p);
      const sentBody = messageText.trim();
      const controller = sendAbort.begin();
      try {
        const res = await apiRequest(
          "POST",
          `/api/app/members/${selectedMemberId}/messages`,
          { messageText: sentBody, autoSend: true, attachmentUrls },
          { signal: controller.signal },
        );
        return { data: await res.json(), sentBody };
      } finally {
        sendAbort.clear(controller);
      }
    },
    onSuccess: (result) => {
      if (!isMountedRef.current) return;
      queryClient.invalidateQueries({ queryKey: ["/api/app/inbox"] });
      if (selectedMemberId) {
        queryClient.invalidateQueries({ queryKey: ["/api/app/members", selectedMemberId, "timeline"] });
      }
      // Task #2120 — Record canned-response usage only after the send
      // actually succeeded, and only for insertions whose text is still
      // present in the sent body.
      recordCannedUsesIfPresent(pendingCannedUses, result.sentBody);
      setPendingCannedUses([]);
      toast({ title: "Message queued for send" });
      onOpenChange(false);
      if (selectedMemberId) onSent?.(selectedMemberId);
    },
    onError: (err: any) => {
      if (isAbortError(err)) return;
      // Task #951 — Surface attachment-quota 429s as a friendly toast +
      // inline countdown banner instead of the generic "failed to send"
      // error. We keep the modal open and the composer state intact so
      // the host can resend the same message after the cooldown.
      const armed = rateLimit.armFromError(err);
      if (armed) {
        toast({
          title: "Attachment limit reached",
          description: armed.message,
          variant: "destructive",
        });
        return;
      }
      // PadSplit delivery failure — show the dedicated pop-up directing the
      // host to send through PadSplit directly, instead of a generic toast.
      if (isPadsplitSendError(err)) {
        setPadsplitErrorOpen(true);
        return;
      }
      toast({
        title: "Failed to send message",
        description: parseErrorMessage(err, "Couldn't send this message. Please try again."),
        variant: "destructive",
      });
    },
  });

  // The quota is attachment-specific (URLs/hour or bytes/hour), so a
  // text-only retry is still allowed during the cooldown — only sends
  // that include attachments are blocked.
  const sendBlockedByRateLimit = rateLimit.isActive && readyAttachments.length > 0;

  const canSend =
    !!selectedMemberId &&
    (messageText.trim().length > 0 || readyAttachments.length > 0) &&
    !hasUploading &&
    !hasErrors &&
    !sendMutation.isPending &&
    !sendBlockedByRateLimit;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Task #2360: don't let a stray backdrop click / Escape silently
        // drop an in-flight attachment upload OR send. `hasUploading`
        // covers the presigned-URL + S3 PUT flow inside handleFiles;
        // `sendMutation.isPending` covers the final enqueue.
        if (!o && (sendMutation.isPending || hasUploading)) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="modal-new-message"
        onInteractOutside={(e) => {
          if (sendMutation.isPending || hasUploading) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (sendMutation.isPending || hasUploading) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          if (sendMutation.isPending || hasUploading) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>New Message</DialogTitle>
          <DialogDescription>
            Pick a member and send them a direct message.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium mb-2 block">Member</Label>
            {selectedMember ? (
              <div
                className="flex items-center justify-between gap-2 border rounded-md px-3 py-2 bg-blue-50 border-blue-200"
                data-testid="selected-member"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <User className="h-4 w-4 text-blue-500 shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" data-testid="text-selected-member-name">
                      {selectedMember.name}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {selectedMember.phone || selectedMember.email || "No contact info"}
                      {getPropertyLabel(selectedMember) ? ` — ${getPropertyLabel(selectedMember)}` : ""}
                    </div>
                  </div>
                </div>
                {!presetMemberId && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedMemberId(null)}
                    data-testid="button-clear-selected-member"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ) : (
              <>
                <div className="relative mb-2">
                  <Search className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search by name, phone, or property..."
                    className="pl-8"
                    data-testid="input-member-search"
                  />
                </div>
                {membersQuery.isLoading ? (
                  <div className="text-sm text-gray-400 py-2">Loading members...</div>
                ) : filteredMembers.length === 0 ? (
                  <div className="text-sm text-gray-400 py-2" data-testid="empty-member-search">
                    {allMembers.length === 0 ? "No members found." : "No members match your search."}
                  </div>
                ) : (
                  <div
                    className="border rounded-md max-h-60 overflow-y-auto divide-y"
                    data-testid="member-search-list"
                  >
                    {filteredMembers.slice(0, 100).map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        onClick={() => setSelectedMemberId(m.id)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center gap-2"
                        data-testid={`button-pick-member-${m.id}`}
                      >
                        <User className="h-4 w-4 text-gray-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{getMemberDisplayName(m)}</div>
                          <div className="text-xs text-gray-500 truncate">
                            {m.phone || m.email || "No contact info"}
                            {getPropertyLabel(m) ? ` — ${getPropertyLabel(m)}` : ""}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {messagingOutage && <MessagingOutageBanner />}

          {!messagingOutage && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label htmlFor="new-message-text" className="text-sm font-medium">
                Message
              </Label>
              {(() => {
                const selM = selectedMemberId ? allMembers.find((x) => x.id === selectedMemberId) : undefined;
                const selFullName = selM ? getMemberDisplayName(selM) : undefined;
                const selFirstName = selFullName ? selFullName.split(" ")[0] : undefined;
                const selPropId = selM ? resolveCurrentPropertyId(selM) : null;
                const selPropName = selPropId ? (propertyById.get(selPropId)?.name ?? undefined) : undefined;
                return (
                  <CannedResponsePicker
                    onInsert={insertAtCursor}
                    memberFirstName={selFirstName}
                    memberFullName={selFullName}
                    propertyName={selPropName}
                  />
                );
              })()}
            </div>
            <Textarea
              ref={messageTextareaRef}
              id="new-message-text"
              placeholder="Type your message here..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
                  e.preventDefault();
                  if (canSend) sendMutation.mutate();
                }
              }}
              rows={4}
              className="resize-none"
              data-testid="textarea-new-message"
            />
            <MessagePlaceholderPreview
              content={messageText}
              recipientContext={recipientContext}
              recipientName={selectedMember ? getMemberDisplayName(selectedMember) : null}
            />
          </div>
          )}

          {!messagingOutage && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-sm font-medium block">Attachments (optional)</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={attachments.length >= ATTACHMENT_MAX_COUNT || sendMutation.isPending}
                data-testid="button-add-attachment"
              >
                <Paperclip className="h-4 w-4 mr-1.5" />
                Add file
              </Button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,application/pdf"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="input-attachment-file"
            />
            {attachments.length === 0 ? (
              <p className="text-xs text-gray-400">
                Attach images or PDFs (up to {ATTACHMENT_MAX_COUNT}, {Math.round(ATTACHMENT_MAX_BYTES / (1024 * 1024))}MB each).
              </p>
            ) : (
              <ul className="space-y-1.5" data-testid="list-attachments">
                {attachments.map((a) => {
                  const isImage = a.mimeType.startsWith("image/");
                  return (
                    <li
                      key={a.id}
                      className={`flex items-center gap-2 border rounded-md px-2.5 py-1.5 text-sm ${
                        a.status === "error"
                          ? "border-red-200 bg-red-50"
                          : "border-gray-200 bg-gray-50"
                      }`}
                      data-testid={`attachment-item-${a.id}`}
                    >
                      {isImage ? (
                        <ImageIcon className="h-4 w-4 text-gray-500 shrink-0" />
                      ) : (
                        <FileText className="h-4 w-4 text-gray-500 shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium" data-testid={`text-attachment-name-${a.id}`}>
                          {a.name}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {formatBytes(a.size)}
                          {a.status === "uploading" && " · Uploading..."}
                          {a.status === "ready" && " · Ready"}
                          {a.status === "error" && a.errorMessage ? ` · ${a.errorMessage}` : ""}
                        </div>
                      </div>
                      {a.status === "uploading" && (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin shrink-0" />
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeAttachment(a.id)}
                        className="h-7 w-7 p-0"
                        data-testid={`button-remove-attachment-${a.id}`}
                        aria-label={a.status === "uploading" ? "Cancel upload" : "Remove attachment"}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
            {hasErrors && (
              <p className="text-xs text-red-500 mt-1.5" data-testid="text-attachment-error-summary">
                Remove failed attachments before sending.
              </p>
            )}
          </div>
          )}

          {rateLimit.isActive && (
            <div
              className="flex items-start gap-2 border border-amber-200 bg-amber-50 dark:border-amber-900 dark:bg-amber-950 rounded-md px-3 py-2 text-sm text-amber-900 dark:text-amber-100"
              data-testid="banner-new-message-rate-limit"
            >
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" />
              <div className="flex-1 min-w-0">
                <div className="font-medium" data-testid="text-new-message-rate-limit-message">
                  {rateLimit.message || "Attachment limit reached."}
                </div>
                <div className="text-xs mt-0.5">
                  Try again in{" "}
                  <span
                    className="font-semibold tabular-nums"
                    data-testid="text-new-message-rate-limit-countdown"
                  >
                    {formatRateLimitCountdown(rateLimit.remaining)}
                  </span>
                  . Your message and attachments are still here.
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                // Task #2367 — explicit cancel tears down any in-flight
                // send + attachment uploads before closing the modal,
                // so the user isn't stuck waiting for a multi-second
                // request they no longer want.
                sendAbort.cancel();
                cancelAllAttachmentUploads();
                onOpenChange(false);
              }}
              data-testid="button-new-message-cancel"
            >
              {sendMutation.isPending || hasUploading ? "Cancel" : "Close"}
            </Button>
            {!messagingOutage && (
            <Button
              onClick={() => sendMutation.mutate()}
              disabled={!canSend}
              title="Send (Ctrl+Enter)"
              data-testid="button-new-message-send"
            >
              <Send className="h-4 w-4 mr-1.5" />
              {sendMutation.isPending
                ? "Sending..."
                : sendBlockedByRateLimit && rateLimit.remaining > 0
                  ? `Try again in ${formatRateLimitCountdown(rateLimit.remaining)}`
                  : "Send"}
            </Button>
            )}
          </div>
        </div>
      </DialogContent>
      <PadsplitSendErrorDialog open={padsplitErrorOpen} onOpenChange={setPadsplitErrorOpen} />
    </Dialog>
  );
}
